import React, { useState, useEffect } from 'react';
import { Link } from 'react-router';
import { toast } from 'react-toastify';
import { X } from 'lucide-react';
import {
  getPaymentByAppointmentApi,
  recordPaymentApi,
  cancelPatientDebtsApi,
  getPatientPaymentHistoryApi
} from '../../services/apiService';
import '../consultation/tabs/PaymentManagementTab.css';
import AddPaymentModal from './modals/AddPaymentModal';

/**
 * PaymentQuickAccess Component
 * 
 * A quick access card for managing patient payments directly from the Patient Dictionary page.
 * Displays payment summary for the last appointment and provides quick actions for:
 * - Recording new payments
 * - Canceling debts (commercial gesture)
 * 
 * @param {Object} props
 * @param {string} props.patientId - The patient's ID
 * @param {string} props.childId - Optional child patient ID
 * @param {Object} props.allPatientDetails - Patient details object containing appointments
 * @param {Object} props.allPatientChildDetails - Child patient details (if applicable)
 * @param {Function} props.onPaymentUpdate - Callback function called after payment update
 */
const PaymentQuickAccess = ({
  patientId,
  childId,
  allPatientDetails,
  allPatientChildDetails,
  onPaymentUpdate
}) => {
  const [lastPaymentData, setLastPaymentData] = useState(null);
  const [allPaymentsSummary, setAllPaymentsSummary] = useState(null);
  const [showQuickPaymentModal, setShowQuickPaymentModal] = useState(false);
  const [quickPaymentAmount, setQuickPaymentAmount] = useState('');
  const [quickPaymentMethod, setQuickPaymentMethod] = useState('Espèces');
  const [quickPaymentNote, setQuickPaymentNote] = useState('');
  const [quickPaymentLoading, setQuickPaymentLoading] = useState(false);
  const [showQuickCancelDebtModal, setShowQuickCancelDebtModal] = useState(false);
  const [quickCancelDebtAmount, setQuickCancelDebtAmount] = useState('');
  const [quickCancelDebtNote, setQuickCancelDebtNote] = useState('');

  /**
   * Fetch payment data for the most recent appointment
   */
  const getLastPaymentData = async () => {
    try {
      // Get the most recent appointment from patient details
      let lastAppointmentId = null;
      
      if (childId && allPatientChildDetails?.Appointment?.length > 0) {
        lastAppointmentId = allPatientChildDetails.Appointment[0]?.id;
      } else if (allPatientDetails?.Appointment?.length > 0) {
        lastAppointmentId = allPatientDetails.Appointment[0]?.id;
      }
      
      if (!lastAppointmentId) {
        console.log('No appointment ID found for payment quick access');
        return;
      }
      
      const response = await getPaymentByAppointmentApi(lastAppointmentId);
      const payment = response.data.data || response.data;
      
      if (payment) {
        setLastPaymentData({
          ...payment,
          appointmentId: lastAppointmentId
        });
      }
    } catch (err) {
      console.error('Error fetching last payment:', err);
    }
  };

  /**
   * Fetch all payments for the patient across all appointments
   */
  const getAllPaymentsSummary = async () => {
    if (!patientId) return;
    
    try {
      const response = await getPatientPaymentHistoryApi(patientId, true);
      const responseData = response.data.data || response.data;
      const paymentsArray = responseData.payments || [];
      
      // Calculate totals across all payments
      const totalAmount = paymentsArray.reduce((sum, p) => sum + parseFloat(p.total_amount || 0), 0);
      const totalPaid = paymentsArray.reduce((sum, p) => sum + parseFloat(p.paid_amount || 0), 0);
      const totalDebt = paymentsArray.reduce((sum, p) => sum + parseFloat(p.debt_amount || 0), 0);
      
      setAllPaymentsSummary({
        totalAmount,
        totalPaid,
        totalDebt,
        paymentsCount: paymentsArray.length
      });
    } catch (err) {
      console.error('Error fetching all payments summary:', err);
    }
  };

  /**
   * Handle quick payment submission
   */
  const handleQuickPayment = async () => {
    if (!quickPaymentAmount || parseFloat(quickPaymentAmount) <= 0 || !lastPaymentData) return;

    setQuickPaymentLoading(true);
    try {
      const methodMap = {
        'Espèces': 'cash',
        'Carte bancaire': 'card',
        'Chèque': 'check',
        'Virement': 'transfer',
        'Mobile Money': 'mobile'
      };

      const payload = {
        amount: parseFloat(quickPaymentAmount),
        payment_method: methodMap[quickPaymentMethod] || 'cash',
        notes: quickPaymentNote || 'Paiement rapide'
      };

      await recordPaymentApi(lastPaymentData.id, payload);
      
      toast.success('Paiement enregistré avec succès');
      setShowQuickPaymentModal(false);
      setQuickPaymentAmount('');
      setQuickPaymentNote('');
      setQuickPaymentMethod('Espèces');
      
      // Refresh payment data
      await getLastPaymentData();
      await getAllPaymentsSummary();
      
      // Notify parent component
      if (onPaymentUpdate) {
        onPaymentUpdate();
      }
    } catch (err) {
      console.error('Error recording payment:', err);
      toast.error(err.response?.data?.message || 'Erreur lors de l\'enregistrement du paiement');
    } finally {
      setQuickPaymentLoading(false);
    }
  };

  /**
   * Handle debt cancellation (commercial gesture)
   */
  const handleQuickCancelDebt = async () => {
    if (!quickCancelDebtAmount || parseFloat(quickCancelDebtAmount) <= 0 || !patientId) return;

    const totalDebt = allPaymentsSummary?.totalDebt || 0;
    if (parseFloat(quickCancelDebtAmount) > totalDebt) {
      toast.error('Le montant ne peut pas dépasser la dette totale');
      return;
    }

    setQuickPaymentLoading(true);
    try {
      const payload = {
        amount: parseFloat(quickCancelDebtAmount),
        payment_method: 'other',
        note: quickCancelDebtNote || 'Annulation de dette (Geste commercial)',
        paid_at: new Date().toISOString().split('T')[0]
      };

      await cancelPatientDebtsApi(patientId, payload);
      
      toast.success('Dette annulée avec succès');
      setShowQuickCancelDebtModal(false);
      setQuickCancelDebtAmount('');
      setQuickCancelDebtNote('');
      
      // Refresh payment data
      await getLastPaymentData();
      await getAllPaymentsSummary();
      
      // Notify parent component
      if (onPaymentUpdate) {
        onPaymentUpdate();
      }
    } catch (err) {
      console.error('Error canceling debt:', err);
      toast.error(err.response?.data?.message || 'Erreur lors de l\'annulation de la dette');
    } finally {
      setQuickPaymentLoading(false);
    }
  };

  /**
   * Format currency in Algerian Dinar
   */
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-DZ', { 
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0) + ' DA';
  };

  // Fetch payment data when patient details are loaded
  useEffect(() => {
    if (allPatientDetails?.Appointment?.length > 0 || allPatientChildDetails?.Appointment?.length > 0) {
      getLastPaymentData();
      getAllPaymentsSummary();
    }
  }, [allPatientDetails, allPatientChildDetails, childId, patientId]);

  return (
    <>
      {/* Quick Payment Management Card */}
      <div className="Card" style={{ 
        marginBottom: '20px', 
        border: '2px solid #3b82f6', 
        boxShadow: '0 4px 6px rgba(59, 130, 246, 0.1)',
        minHeight: 'auto' // Fix: ensure height fits content
      }}>
        <div style={{ padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: 0, color: '#1e40af', fontSize: '18px', fontWeight: '600' }}>
              💳 Raccourci Paiement (Dernier RDV)
            </h3>
            {lastPaymentData?.appointmentId && (
              <Link 
                to={`/doctor/appointment-details/${lastPaymentData.appointmentId}`}
                style={{ 
                  fontSize: '14px', 
                  color: '#3b82f6', 
                  textDecoration: 'none',
                  fontWeight: '500'
                }}
              >
                Voir détails →
              </Link>
            )}
          </div>

          {lastPaymentData ? (
            <>
              {/* Total summary across ALL appointments */}
              {allPaymentsSummary && (
                <div style={{ 
                  backgroundColor: allPaymentsSummary.totalDebt > 0 ? '#fef2f2' : allPaymentsSummary.totalDebt < 0 ? '#f0fdf4' : '#f9fafb',
                  padding: '16px', 
                  borderRadius: '8px', 
                  marginBottom: '12px',
                  border: `2px solid ${allPaymentsSummary.totalDebt > 0 ? '#fecaca' : allPaymentsSummary.totalDebt < 0 ? '#bbf7d0' : '#e5e7eb'}`
                }}>
                  <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <p style={{ margin: 0, fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>
                      📊 Bilan Global ({allPaymentsSummary.paymentsCount} RDV)
                    </p>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                    <div>
                      <p style={{ margin: 0, fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
                        Total Facturé
                      </p>
                      <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                        {formatCurrency(allPaymentsSummary.totalAmount)}
                      </p>
                    </div>
                    <div>
                      <p style={{ margin: 0, fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
                        Total Payé
                      </p>
                      <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#059669' }}>
                        {formatCurrency(allPaymentsSummary.totalPaid)}
                      </p>
                    </div>
                  </div>
                  <div style={{ 
                    backgroundColor: 'white',
                    padding: '12px',
                    borderRadius: '6px',
                    textAlign: 'center'
                  }}>
                    <p style={{ margin: 0, fontSize: '12px', color: '#6b7280', marginBottom: '4px', fontWeight: '600' }}>
                      {allPaymentsSummary.totalDebt > 0 ? '⚠️ DETTE TOTALE' : allPaymentsSummary.totalDebt < 0 ? '✅ CRÉDIT PATIENT' : '✅ SOLDÉ'}
                    </p>
                    <p style={{ 
                      margin: 0, 
                      fontSize: '24px', 
                      fontWeight: '700', 
                      color: allPaymentsSummary.totalDebt > 0 ? '#dc2626' : allPaymentsSummary.totalDebt < 0 ? '#059669' : '#6b7280'
                    }}>
                      {allPaymentsSummary.totalDebt < 0 
                        ? `+${formatCurrency(Math.abs(allPaymentsSummary.totalDebt))}` 
                        : formatCurrency(allPaymentsSummary.totalDebt)
                      }
                    </p>
                  </div>
                </div>
              )}

              {/* Last appointment details */}
              <div style={{ 
                backgroundColor: '#eff6ff', 
                padding: '14px', 
                borderRadius: '8px', 
                marginBottom: '16px',
                border: '1px solid #dbeafe'
              }}>
                <p style={{ margin: '0 0 10px 0', fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>
                  📅 Dernier RDV
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                  <div>
                    <p style={{ margin: 0, fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
                      Total
                    </p>
                    <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                      {formatCurrency(lastPaymentData.total_amount || 0)}
                    </p>
                  </div>
                  <div>
                    <p style={{ margin: 0, fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
                      Payé
                    </p>
                    <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#059669' }}>
                      {formatCurrency(lastPaymentData.paid_amount || 0)}
                    </p>
                  </div>
                  <div>
                    <p style={{ margin: 0, fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
                      Dette
                    </p>
                    <p style={{ 
                      margin: 0, 
                      fontSize: '14px', 
                      fontWeight: '600', 
                      color: (lastPaymentData.debt_amount || 0) > 0 ? '#dc2626' : '#059669' 
                    }}>
                      {formatCurrency(lastPaymentData.debt_amount || 0)}
                    </p>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => {
                    setQuickPaymentAmount('');
                    setQuickPaymentNote('');
                    setQuickPaymentMethod('Espèces');
                    setShowQuickPaymentModal(true);
                  }}
                  style={{
                    flex: 1,
                    padding: '10px 16px',
                    backgroundColor: '#059669',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <span style={{ fontSize: '16px' }}>💰</span>
                  Ajouter Paiement
                </button>
                
                {allPaymentsSummary && allPaymentsSummary.totalDebt > 0 && (
                  <>
                    <button
                      onClick={() => {
                        setQuickPaymentAmount(allPaymentsSummary.totalDebt.toString());
                        setQuickPaymentNote('Règlement de dette totale');
                        setQuickPaymentMethod('Espèces');
                        setShowQuickPaymentModal(true);
                      }}
                      style={{
                        flex: 1,
                        padding: '10px 16px',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                    >
                      <span style={{ fontSize: '16px' }}>💳</span>
                      Régler Dette
                    </button>
                    <button
                      onClick={() => {
                        setQuickCancelDebtAmount('');
                        setQuickCancelDebtNote('');
                        setShowQuickCancelDebtModal(true);
                      }}
                      style={{
                        flex: 1,
                        padding: '10px 16px',
                        backgroundColor: '#f59e0b',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                    >
                      <span style={{ fontSize: '16px' }}>❌</span>
                      Annuler Dette
                    </button>
                  </>
                )}
              </div>
            </>
          ) : (
            <div style={{ 
              backgroundColor: '#fef3c7', 
              padding: '16px', 
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <p style={{ margin: 0, color: '#92400e', fontSize: '14px' }}>
                📋 Chargement des informations de paiement...
              </p>
              <p style={{ margin: '8px 0 0 0', color: '#92400e', fontSize: '12px' }}>
                {!allPatientDetails ? 'En attente des données patient' : 'Aucun rendez-vous trouvé'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Payment Modal */}
      <AddPaymentModal
        show={showQuickPaymentModal}
        onClose={() => {
          setShowQuickPaymentModal(false);
          setQuickPaymentAmount('');
          setQuickPaymentNote('');
          setQuickPaymentMethod('Espèces');
        }}
        onConfirm={async (paymentData) => {
          if (!lastPaymentData) return;
          
          setQuickPaymentLoading(true);
          try {
            const methodMap = {
              'Espèces': 'cash',
              'Carte bancaire': 'card',
              'Chèque': 'check',
              'Virement': 'transfer',
              'Mobile Money': 'mobile'
            };

            const payload = {
              amount: paymentData.amount,
              payment_method: methodMap[paymentData.paymentMethod] || 'cash',
              notes: paymentData.note || 'Paiement rapide'
            };

            await recordPaymentApi(lastPaymentData.id, payload);
            
            toast.success('Paiement enregistré avec succès');
            setShowQuickPaymentModal(false);
            setQuickPaymentAmount('');
            setQuickPaymentNote('');
            setQuickPaymentMethod('Espèces');
            
            // Refresh payment data
            await getLastPaymentData();
            await getAllPaymentsSummary();
            
            // Notify parent component
            if (onPaymentUpdate) {
              onPaymentUpdate();
            }
          } catch (err) {
            console.error('Error recording payment:', err);
            toast.error(err.response?.data?.message || 'Erreur lors de l\'enregistrement du paiement');
          } finally {
            setQuickPaymentLoading(false);
          }
        }}
        currentPayment={lastPaymentData ? {
          appointmentId: lastPaymentData.appointmentId,
          debt: lastPaymentData.debt_amount || 0,
          totalAmount: lastPaymentData.total_amount || 0,
          paidAmount: lastPaymentData.paid_amount || 0
        } : null}
        statistics={{ totalDebt: allPaymentsSummary?.totalDebt || lastPaymentData?.debt_amount || 0 }}
        formatCurrency={formatCurrency}
        initialPaymentType="normal"
        initialAmount={quickPaymentAmount}
        initialNote={quickPaymentNote}
        initialMethod={quickPaymentMethod}
      />

      {/* Quick Cancel Debt Modal */}
      {showQuickCancelDebtModal && (
        <div className="pmt-modal-backdrop">
          <div className="pmt-modal">
            <div className="pmt-modal-header">
              <h2 className="pmt-modal-title">Annuler la Dette</h2>
              <button className="pmt-modal-close" onClick={() => setShowQuickCancelDebtModal(false)}>
                <X size={24} />
              </button>
            </div>
            <div className="pmt-modal-body">
              {allPaymentsSummary && (
                <div className="pmt-info-box blue">
                  <p className="pmt-info-box-label">Dette totale (Tous RDV):</p>
                  <p className="pmt-info-box-amount red">{formatCurrency(allPaymentsSummary.totalDebt)}</p>
                  <p style={{ fontSize: '0.75rem', color: '#991b1b', marginTop: '0.5rem' }}>
                    ⚠️ L'annulation de dette est un geste commercial sans encaissement réel.
                  </p>
                </div>
              )}

              <div className="pmt-form-group">
                <label className="pmt-label">
                  Montant à annuler (Max: {formatCurrency(allPaymentsSummary?.totalDebt || 0)})
                </label>
                <input
                  type="number"
                  step="0.01"
                  max={allPaymentsSummary?.totalDebt || 0}
                  value={quickCancelDebtAmount}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    if (value <= (allPaymentsSummary?.totalDebt || 0)) {
                      setQuickCancelDebtAmount(e.target.value);
                    }
                  }}
                  placeholder="0.00"
                  className="pmt-input"
                />
                {quickCancelDebtAmount && parseFloat(quickCancelDebtAmount) > (allPaymentsSummary?.totalDebt || 0) && (
                  <p style={{ color: '#dc2626', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                    ⚠️ Le montant ne peut pas dépasser la dette totale
                  </p>
                )}
              </div>

              <div className="pmt-form-group">
                <label className="pmt-label">Raison de l'annulation</label>
                <textarea
                  value={quickCancelDebtNote}
                  onChange={(e) => setQuickCancelDebtNote(e.target.value)}
                  rows={3}
                  placeholder="Ex: Geste commercial, situation difficile du patient..."
                  className="pmt-textarea"
                />
              </div>

              {quickCancelDebtAmount && parseFloat(quickCancelDebtAmount) > 0 && allPaymentsSummary && parseFloat(quickCancelDebtAmount) <= allPaymentsSummary.totalDebt && (
                <div className="pmt-info-box green">
                  <p className="pmt-info-box-label">Dette restante après annulation:</p>
                  <p className="pmt-info-box-amount green">
                    {formatCurrency(allPaymentsSummary.totalDebt - parseFloat(quickCancelDebtAmount))}
                  </p>
                </div>
              )}

              <div className="pmt-modal-actions">
                <button
                  onClick={handleQuickCancelDebt}
                  disabled={!quickCancelDebtAmount || parseFloat(quickCancelDebtAmount) <= 0 || parseFloat(quickCancelDebtAmount) > (allPaymentsSummary?.totalDebt || 0) || quickPaymentLoading}
                  className="pmt-btn pmt-btn-secondary"
                >
                  <X size={16} />
                  {quickPaymentLoading ? 'En cours...' : 'Confirmer l\'Annulation'}
                </button>
                <button
                  onClick={() => setShowQuickCancelDebtModal(false)}
                  disabled={quickPaymentLoading}
                  className="pmt-btn pmt-btn-primary"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PaymentQuickAccess;
